import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import { graphql } from "gatsby"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import { IArticle } from "../molecules/article-card"

/**
 * Converts contentful compose page data to a simpler IArticle object
 * @param data - contentful compose page data
 * @returns IArticle object
 */
export const contentfulComposePageToArticleCard = (data: any) => {
  const article: IArticle = data
    ? {
        title: data.title,
        slug: data.slug,
        date: data.content.publishDate || data.content.updatedAt,
        content: data.content.content
          ? documentToPlainTextString(JSON.parse(data.content.content.raw))
          : "",
        category: data.content.tags.category,
        topic: data.content.tags.topic,
        type: data.content.articleType,
        image: data.content.image ? data.content.image.fluid : undefined,
      }
    : {
        title: "undefined",
        slug: "",
        date: "undefined",
        image: undefined,
        content: "undefined",
        category: { name: "undefined", slug: "undefined" },
        topic: { name: "undefined", slug: "undefined" },
      }
  return article
}

/**
 * Converts Contentful BlogPost data to a simpler IArticle object
 * @param data - contentful compose page data
 * @returns IArticle object
 */
export const contentfulBlogPostToArticleCard = (data: any) => {
  if (!data) {
    return {} as IArticle
  }

  const article: IArticle = {
    title: data.compose__page ? data.compose__page[0].title : "Not Found",
    slug: data.compose__page ? data.compose__page[0].slug : "not-found",
    date: data.publishDate || data.updatedAt,
    content: data.content
      ? documentToPlainTextString(JSON.parse(data.content.raw))
      : "",
    category: data.tags
      ? data.tags.category
      : { name: "undefined", slug: "not-found" },
    topic: data.tags
      ? data.tags.topic
      : { name: "undefined", slug: "not-found" },
    type: data.articleType,
    image: data.image ? data.image.fluid : undefined,
  }

  return article
}

/**
 * Cuts the text at the given length and adds ellipses
 * @param {string} content - The content to truncate
 * @returns Truncated text
 */
export const truncateContent = (content: string) => {
  const maxWords = 30
  const words = content.split(" ")
  if (words.length <= maxWords) {
    return content
  }
  const truncatedContent = words.slice(0, maxWords).join(" ") + "..."
  return truncatedContent
}

/**
 * GraphQL fragment for getting blog post data from Contentful via BlogPost content type
 */
export const ContentfulBlogPostFragment = graphql`
  fragment ContentfulBlogPostFragment on ContentfulBlogPost {
    publishDate(formatString: "MMM D, YYYY")
    updatedAt(formatString: "MMM D, YYYY")
    compose__page {
      title
      slug
    }
    tags {
      category {
        name
        slug
      }
      topic {
        name
        slug
      }
    }
    content {
      raw
    }
    articleType
    image {
      fluid(maxWidth: 480, quality: 90) {
        ...GatsbyContentfulFluid_withWebp
      }
    }
  }
`

/**
 * GraphQL fragment for getting Blog Post data from Contentful via ComposePage content type
 */
export const ContentfulComposePageFragment = graphql`
  fragment ContentfulComposePageFragment on ContentfulComposePage {
    title
    slug
    content {
      ... on ContentfulBlogPost {
        __typename
        author
        publishDate(formatString: "MMM D, YYYY")
        updatedAt(formatString: "MMM D, YYYY")
        tags {
          category {
            name
            slug
          }
          topic {
            name
            slug
          }
        }
        content {
          raw
        }
        articleType
        image {
          fluid(maxWidth: 480) {
            ...GatsbyContentfulFluid
          }
        }
      }
    }
  }
`

export const richTextObjectToString = (
  richText: RenderRichTextData<ContentfulRichTextGatsbyReference>
) => {
  let summary = ""
  try {
    summary = documentToPlainTextString(JSON.parse(richText.raw))
  } catch (e) {
    // do nothing
  }
  return summary
}
